import {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Text,
    Badge,
    SimpleGrid,
    Image,
    Flex,
    Grid,
    Dialog,
    Button,
    Input,
} from "@chakra-ui/react";

import {
    Pencil,
    Save,
    X,
} from "lucide-react";

import PdfIcon from "../../../assets/pdf.png";
import AppDrawer from "../../../components/AppDrawer";

const BASE_URL =
    process.env.REACT_APP_API_BASE_URL;

const ViewVehicleDrawer = ({
    vehicle,
    open,
    onClose,
    onEdit,
    onUpdate,
}) => {

    const [
        selectedMedia,
        setSelectedMedia,
    ] = useState(null);

    const [
        isEditing,
        setIsEditing,
    ] = useState(false);

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        form,
        setForm,
    ] = useState({});


    useEffect(() => {

        if (!vehicle) {
            return;
        }

        setForm({
            registration_number:
                vehicle.registration_number || "",

            vehicle_make:
                vehicle.vehicle_make || "",

            vehicle_model:
                vehicle.vehicle_model || "",

            vehicle_body_type:
                vehicle.vehicle_body_type || "",

            vehicle_color:
                vehicle.vehicle_color || "",

            chasis_number:
                vehicle.chasis_number || "",

            engine_number:
                vehicle.engine_number || "",

            year_of_manufacturing:
                vehicle.year_of_manufacturing
                    ? String(
                        vehicle.year_of_manufacturing
                    ).substring(
                        0,
                        10
                    )
                    : "",

            blue_book: null,
            vehicle_image: null,
        });

        setIsEditing(false);

    }, [vehicle]);


    if (!vehicle) {
        return null;
    }


    const vehicleDocuments =
        vehicle.documents?.filter(
            doc =>
                [
                    "Blue Book",
                    "Vehicle Image",
                ].includes(
                    doc.document_type
                )
        ) || [];


    const fieldMap = {
        "Blue Book":
            "blue_book",

        "Vehicle Image":
            "vehicle_image",
    };


    const handleChange =
        (
            field,
            value
        ) => {

            setForm(
                previous => ({
                    ...previous,
                    [field]: value,
                })
            );
        };


const handleReplace = (
    doc,
    file
) => {
    if (!file) {
        return;
    }

    const field =
        fieldMap[
            doc.document_type
        ];

    if (!field) {
        return;
    }

    setForm(previous => ({
        ...previous,
        [field]: [
            {
                file,
            },
        ],
    }));
};
const getDocumentFile =
    doc => {

        const field =
            fieldMap[
                doc.document_type
            ];

        return field
            ? form[field]
            : null;
    };

const getDocumentPreview = (
    doc
) => {

    const newFile =
        getDocumentFile(doc);

    const file =
        newFile?.[0]?.file ||
        newFile ||
        null;

    if (file) {
        return {
            src:
                URL.createObjectURL(
                    file
                ),

            extension:
                file.name
                    ?.split(".")
                    .pop()
                    ?.toLowerCase(),

            isNew: true,
        };
    }

    return {
        src:
            `${BASE_URL}/${doc.file_path}`,

        extension:
            doc.file_extension
                ?.toLowerCase(),

        isNew: false,
    };
};

           
  const getStatusColor =
        status => {

            switch (status) {

                case "Approved":
                    return "green";

                case "Pending":
                    return "orange";

                case "Rejected":
                    return "red";

                case "Suspended":
                    return "red";

                case "Active":
                    return "blue";

                case "Inactive":
                    return "yellow";
                case "Review Required":
                    return "orange";

                default:
                    return "gray";
            }
        };



    const handleEdit =
        () => {

            if (onEdit) {
                onEdit(vehicle);
            }

            setIsEditing(true);
        };


    const handleCancel =
        () => {

            setForm({
                registration_number:
                    vehicle.registration_number || "",

                vehicle_make:
                    vehicle.vehicle_make || "",

                vehicle_model:
                    vehicle.vehicle_model || "",

                vehicle_body_type:
                    vehicle.vehicle_body_type || "",

                vehicle_color:
                    vehicle.vehicle_color || "",

                chasis_number:
                    vehicle.chasis_number || "",

                engine_number:
                    vehicle.engine_number || "",

                year_of_manufacturing:
                    vehicle.year_of_manufacturing
                        ? String(
                            vehicle.year_of_manufacturing
                        ).substring(
                            0,
                            10
                        )
                        : "",

                blue_book: null,
                vehicle_image: null,
            });

            setIsEditing(false);
        };


    const handleSave =
        async () => {

            if (!onUpdate) {
                return;
            }

            try {

                setSaving(true);

                const result =
                    await onUpdate(
                        vehicle,
                        form
                    );

                if (
                    result?.success
                ) {
                    setIsEditing(false);
                }

            } finally {

                setSaving(false);
            }
        };


    const renderField =
        (
            label,
            field
        ) => (

            <Box>

                <Text
                    fontSize="12px"
                    color="gray.500"
                    mb={
                        isEditing
                            ? 1
                            : 0
                    }
                >
                    {label}
                </Text>


                {isEditing ? (

                    <Input
                        size="sm"
                        value={
                            form[field] ||
                            ""
                        }
                        onChange={e =>
                            handleChange(
                                field,
                                e.target.value
                            )
                        }
                    />

                ) : (

                    <Text
                        fontWeight="600"
                        wordBreak="break-word"
                    >
                        {
                            vehicle[field] ||
                            "-"
                        }
                    </Text>

                )}

            </Box>
        );


    return (
        <>

            <AppDrawer
                open={open}
                onClose={onClose}
                title="Vehicle Details"
                width="600px"
            >

                <Box pb={10}>

                    {/* HEADER */}

                    <Flex
                        direction={{
                            base: "column",
                            md: "row",
                        }}
                        align={{
                            base: "stretch",
                            md: "center",
                        }}
                        justify="space-between"
                        gap={4}
                        mb={6}
                    >

                        <Box minW={0}>

                            <Text
                                fontSize="24px"
                                fontWeight="700"
                                color="#1E293B"
                                wordBreak="break-word"
                            >
                                {
                                    vehicle.vehicle_make
                                }{" "}
                                {
                                    vehicle.vehicle_model
                                }
                            </Text>


                            <Text
                                color="gray.500"
                                wordBreak="break-word"
                            >
                                {
                                    vehicle.registration_number
                                }
                            </Text>


                            <Flex
                                gap={2}
                                mt={2}
                                flexWrap="wrap"
                            >

                                <Badge
                                    colorPalette={
                                        getStatusColor(
                                            vehicle.status
                                        )
                                    }
                                >
                                    {vehicle.status ||
                                        "Pending"}
                                </Badge>
                            </Flex>

                        </Box>


                        {vehicle.vehicle_id && (

                            <Flex
                                gap={2}
                                flexShrink={0}
                                alignSelf={{
                                    base: "flex-start",
                                    md: "center",
                                }}
                            >

                                {!isEditing ? (

                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={
                                            handleEdit
                                        }
                                    >
                                        <Pencil
                                            size={15}
                                        />
                                        Edit Vehicle
                                    </Button>

                                ) : (

                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={
                                                handleCancel
                                            }
                                        >
                                            <X
                                                size={15}
                                            />
                                            Cancel
                                        </Button>

                                        <Button
                                            size="sm"
                                            colorPalette="blue"
                                            loading={
                                                saving
                                            }
                                            onClick={
                                                handleSave
                                            }
                                        >
                                            <Save
                                                size={15}
                                            />
                                            Save
                                        </Button>
                                    </>

                                )}

                            </Flex>

                        )}

                    </Flex>


                    {/* VEHICLE INFORMATION */}

                    <Text
                        fontSize="11px"
                        fontWeight="600"
                        color="#64748B"
                        textTransform="uppercase"
                        mb={4}
                    >
                        Vehicle Information
                    </Text>


                    <Grid
                        templateColumns={{
                            base: "1fr",
                            md: "repeat(2, 1fr)",
                        }}
                        gap={4}
                        mb={8}
                    >

                        {renderField(
                            "Registration Number",
                            "registration_number"
                        )}

                        {renderField(
                            "Make",
                            "vehicle_make"
                        )}

                        {renderField(
                            "Model",
                            "vehicle_model"
                        )}

                        {renderField(
                            "Body Type",
                            "vehicle_body_type"
                        )}

                        {renderField(
                            "Color",
                            "vehicle_color"
                        )}

                        {renderField(
                            "Chassis Number",
                            "chasis_number"
                        )}

                        {renderField(
                            "Engine Number",
                            "engine_number"
                        )}


                        <Box>

                            <Text
                                fontSize="12px"
                                color="gray.500"
                                mb={
                                    isEditing
                                        ? 1
                                        : 0
                                }
                            >
                                Year of Manufacturing
                            </Text>


                            {isEditing ? (

                                <Input
                                    size="sm"
                                    type="date"
                                    value={
                                        form.year_of_manufacturing ||
                                        ""
                                    }
                                    onChange={e =>
                                        handleChange(
                                            "year_of_manufacturing",
                                            e.target.value
                                        )
                                    }
                                />

                            ) : (

                                <Text
                                    fontWeight="600"
                                >
                                    {
                                        vehicle.year_of_manufacturing
                                            ? new Date(
                                                vehicle.year_of_manufacturing
                                            ).getFullYear()
                                            : "-"
                                    }
                                </Text>

                            )}

                        </Box>

                    </Grid>


                    {/* DOCUMENTS */}

                    {vehicleDocuments.length > 0 && (

                        <>

                            <Text
                                fontSize="11px"
                                fontWeight="600"
                                color="#64748B"
                                textTransform="uppercase"
                                mb={3}
                            >
                                Documents
                            </Text>


                            <SimpleGrid
                                columns={{
                                    base: 1,
                                    md: 2,
                                }}
                                gap={3}
                            >

                                {vehicleDocuments.map(
                                    doc => {

                                        const preview =
                                            getDocumentPreview(
                                                doc
                                            );

                                        const selectedFile =
                                            getDocumentFile(
                                                doc
                                            );

                                        const isImage =
                                            [
                                                "png",
                                                "jpg",
                                                "jpeg",
                                                "webp",
                                            ].includes(
                                                preview.extension
                                            );


                                        return (
                                            <Box
                                                key={
                                                    doc.document_id
                                                }
                                                border="1px solid"
                                                borderColor="#E2E8F0"
                                                borderRadius="10px"
                                                overflow="hidden"
                                            >

                                                {/* DOCUMENT PREVIEW */}

                                                <Box
                                                    cursor="pointer"
                                                    onClick={() =>
                                                        setSelectedMedia({
                                                            src:
                                                                preview.src,

                                                            title:
                                                                doc.document_type,

                                                            extension:
                                                                preview.extension,
                                                        })
                                                    }
                                                >

                                                    {isImage ? (

                                                        <Image
                                                            src={
                                                                preview.src
                                                            }
                                                            h="180px"
                                                            w="100%"
                                                            objectFit="cover"
                                                            alt={
                                                                doc.document_type
                                                            }
                                                        />

                                                    ) : (

                                                        <Flex
                                                            h="180px"
                                                            justify="center"
                                                            align="center"
                                                            bg="gray.50"
                                                        >

                                                            <Image
                                                                src={
                                                                    PdfIcon
                                                                }
                                                                h="80%"
                                                                w="80%"
                                                                objectFit="contain"
                                                            />

                                                        </Flex>

                                                    )}

                                                </Box>


                                                {/* DOCUMENT INFORMATION */}

                                                <Box p={3}>

                                                    <Flex
                                                        align="center"
                                                        justify="space-between"
                                                        gap={2}
                                                    >

                                                        <Box
                                                            minW={0}
                                                        >

                                                            <Text
                                                                fontWeight="600"
                                                                fontSize="12px"
                                                            >
                                                                {
                                                                    doc.document_type
                                                                }
                                                            </Text>


                                                            <Text
                                                                fontSize="11px"
                                                                color="gray.500"
                                                                wordBreak="break-all"
                                                            >
                                                                {
                                                                    selectedFile
                                                                        ?.name ||
                                                                    doc.file_path
                                                                        ?.split(
                                                                            "/"
                                                                        )
                                                                        .pop()
                                                                }
                                                            </Text>

                                                        </Box>


                                                        {/* REPLACE */}

                                                        {isEditing && (

                                                            <Button
                                                                size="xs"
                                                                variant="ghost"
                                                                color="#2453FF"
                                                                as="label"
                                                                cursor="pointer"
                                                                flexShrink={0}
                                                                onClick={e =>
                                                                    e.stopPropagation()
                                                                }
                                                            >

                                                                <Pencil
                                                                    size={
                                                                        13
                                                                    }
                                                                />

                                                                Replace

                                                                <input
                                                                    type="file"
                                                                    hidden
                                                                    accept={
                                                                        doc.document_type ===
                                                                        "Vehicle Image"
                                                                            ? ".jpg,.jpeg,.png,.webp"
                                                                            : ".jpg,.jpeg,.png,.pdf"
                                                                    }
                                                                    onChange={e => {

                                                                        const file =
                                                                            e.target.files?.[0];

                                                                        if (
                                                                            !file
                                                                        ) {
                                                                            return;
                                                                        }

                                                                        handleReplace(
                                                                            doc,
                                                                            file
                                                                        );

                                                                        e.target.value =
                                                                            "";
                                                                    }}
                                                                />

                                                            </Button>

                                                        )}

                                                    </Flex>


                                                    {/* NEW DOCUMENT INDICATOR */}

                                                    {selectedFile && (

                                                        <Text
                                                            mt={1}
                                                            fontSize="10px"
                                                            color="#2453FF"
                                                            fontWeight="500"
                                                        >
                                                            New document selected
                                                        </Text>

                                                    )}

                                                </Box>

                                            </Box>
                                        );

                                    }
                                )}

                            </SimpleGrid>

                        </>

                    )}

                </Box>

            </AppDrawer>


            {/* DOCUMENT PREVIEW */}

            <Dialog.Root
                open={
                    !!selectedMedia
                }
                onOpenChange={details => {

                    if (!details.open) {
                        setSelectedMedia(null);
                    }

                }}
            >

                <Dialog.Backdrop />

                <Dialog.Positioner
                    p={4}
                >

                    <Dialog.Content
                        w="100%"
                        maxW="800px"
                        maxH="90vh"
                        overflow="hidden"
                    >

                        <Dialog.Header>

                            <Dialog.Title
                                wordBreak="break-word"
                            >
                                {
                                    selectedMedia?.title
                                }
                            </Dialog.Title>

                        </Dialog.Header>


                        <Dialog.Body
                            p={0}
                        >

                            {[
                                "png",
                                "jpg",
                                "jpeg",
                                "webp",
                            ].includes(
                                selectedMedia?.extension
                                    ?.toLowerCase()
                            ) ? (

                                <Image
                                    src={
                                        selectedMedia?.src
                                    }
                                    w="100%"
                                    maxH="80vh"
                                    objectFit="contain"
                                />

                            ) : (

                                <Box
                                    h="80vh"
                                >

                                    <iframe
                                        src={
                                            selectedMedia?.src
                                        }
                                        title={
                                            selectedMedia?.title
                                        }
                                        style={{
                                            width:
                                                "100%",
                                            height:
                                                "100%",
                                            border:
                                                "none",
                                        }}
                                    />

                                </Box>

                            )}

                        </Dialog.Body>

                    </Dialog.Content>

                </Dialog.Positioner>

            </Dialog.Root>

        </>
    );
};

export default ViewVehicleDrawer;